<template>
  <g>
    <!-- Connection Line -->
    <path
      :d="edgePath"
      :style="edgeStyle"
      class="vue-flow__edge-path"
      :marker-end="markerEnd"
    />
  </g>
  
  <!-- Logic Slot Button - Rendered outside SVG using EdgeLabelRenderer -->
  <EdgeLabelRenderer>
    <div
      :style="{
        position: 'absolute',
        transform: `translate(-50%, -50%) translate(${centerX}px, ${centerY}px)`,
        pointerEvents: 'all'
      }"
      class="logic-slot-container"
    >
        <!-- State 3: Logic Card Display (after drop) -->
        <div
          v-if="data.logicCardType"
          class="logic-card"
          :class="{
            'logic-card--not-configured': !isLogicCardConfigured,
            'logic-card--configured': isLogicCardConfigured,
            'logic-card--hover': isLogicCardHovering,
            'logic-card--selected': isLogicCardSelected
          }"
          @mouseenter="handleLogicCardMouseEnter"
          @mouseleave="handleLogicCardMouseLeave"
          @click="handleLogicCardClick"
        >
          <div class="logic-card-content">
            <!-- Wait Icon -->
            <div v-if="logicCardType === 'wait'" class="logic-card-icon-wrapper">
              <svg xmlns="http://www.w3.org/2000/svg" width="17" height="20" viewBox="0 0 17 20" fill="none" class="logic-card-icon">
                <path d="M11.3333 0H5.66667V1.90476H11.3333V0ZM7.55556 12.381H9.44444V6.66667H7.55556V12.381ZM15.1394 6.08571L16.4806 4.73333C16.0744 4.24762 15.6306 3.79048 15.1489 3.39048L13.8078 4.74286C12.3439 3.5619 10.5022 2.85714 8.5 2.85714C3.80611 2.85714 0 6.69524 0 11.4286C0 16.1619 3.79667 20 8.5 20C13.2033 20 17 16.1619 17 11.4286C17 9.40952 16.3011 7.55238 15.1394 6.08571ZM8.5 18.0952C4.845 18.0952 1.88889 15.1143 1.88889 11.4286C1.88889 7.74286 4.845 4.7619 8.5 4.7619C12.155 4.7619 15.1111 7.74286 15.1111 11.4286C15.1111 15.1143 12.155 18.0952 8.5 18.0952Z" fill="white"/>
              </svg>
            </div>
            
            <!-- Review & Approval Icon -->
            <div v-else class="logic-card-icon-wrapper">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="20" viewBox="0 0 16 20" fill="none" class="logic-card-icon">
                <path d="M8 20C5.68333 19.4167 3.77083 18.0875 2.2625 16.0125C0.754167 13.9375 0 11.6333 0 9.1V3L8 0L16 3V9.1C16 10.5167 15.7583 11.8792 15.275 13.1875C14.7917 14.4958 14.1 15.65 13.2 16.65L10 13.45C9.7 13.6333 9.37917 13.7708 9.0375 13.8625C8.69583 13.9542 8.35 14 8 14C6.9 14 5.95833 13.6083 5.175 12.825C4.39167 12.0417 4 11.1 4 10C4 8.9 4.39167 7.95833 5.175 7.175C5.95833 6.39167 6.9 6 8 6C9.1 6 10.0417 6.39167 10.825 7.175C11.6083 7.95833 12 8.9 12 10C12 10.3667 11.9542 10.7208 11.8625 11.0625C11.7708 11.4042 11.6333 11.7333 11.45 12.05L12.95 13.55C13.2833 12.8667 13.5417 12.15 13.725 11.4C13.9083 10.65 14 9.88333 14 9.1V4.375L8 2.125L2 4.375V9.1C2 11.1167 2.56667 12.95 3.7 14.6C4.83333 16.25 6.26667 17.35 8 17.9C8.43333 17.7667 8.84583 17.5958 9.2375 17.3875C9.62917 17.1792 10.0167 16.9333 10.4 16.65L11.8 18.05C11.25 18.5 10.6542 18.8917 10.0125 19.225C9.37083 19.5583 8.7 19.8167 8 20ZM8 12C8.55 12 9.02083 11.8042 9.4125 11.4125C9.80417 11.0208 10 10.55 10 10C10 9.45 9.80417 8.97917 9.4125 8.5875C9.02083 8.19583 8.55 8 8 8C7.45 8 6.97917 8.19583 6.5875 8.5875C6.19583 8.97917 6 9.45 6 10C6 10.55 6.19583 11.0208 6.5875 11.4125C6.97917 11.8042 7.45 12 8 12Z" fill="white"/>
              </svg>
            </div>
            
            <div class="logic-card-label">{{ logicCardLabel }}</div>
          </div>
          
          <!-- Warning Icon for Not Configured State -->
          <img
            v-if="!isLogicCardConfigured"
            src="@/assets/MyWorkBench/Workflow/warning_icon1.svg"
            alt="Warning"
            class="logic-card-warning"
          />
        </div>
        
        <!-- State 0: Default - Icon Only -->
        <el-tooltip
          v-else-if="!isExpanded"
          :visible="isHovering && !isExpanded"
          placement="right"
          effect="dark"
          :offset="8"
          popper-class="logic-slot-tooltip"
        >
          <template #content>
            <div class="tooltip-content">
              Add a logic block.<br />
              Logic block is optional. But you can use them to control your workflow.
            </div>
          </template>
          <div
            class="logic-slot-button"
            :class="{ 'logic-slot-button--hover': isHovering && !isExpanded }"
            @mouseenter="handleMouseEnter"
            @mouseleave="handleMouseLeave"
            @click="handleSlotClick"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M7.5 17.5C6.58333 17.5 5.79861 17.1736 5.14583 16.5208C4.49306 15.8681 4.16667 15.0833 4.16667 14.1667V7.35417C3.68056 7.17361 3.28125 6.87153 2.96875 6.44792C2.65625 6.02431 2.5 5.54167 2.5 5C2.5 4.30556 2.74306 3.71528 3.22917 3.22917C3.71528 2.74306 4.30556 2.5 5 2.5C5.69444 2.5 6.28472 2.74306 6.77083 3.22917C7.25694 3.71528 7.5 4.30556 7.5 5C7.5 5.54167 7.34375 6.02431 7.03125 6.44792C6.71875 6.87153 6.31944 7.17361 5.83333 7.35417V14.1667C5.83333 14.625 5.99653 15.0174 6.32292 15.3438C6.64931 15.6701 7.04167 15.8333 7.5 15.8333C7.95833 15.8333 8.35069 15.6701 8.67708 15.3438C9.00347 15.0174 9.16667 14.625 9.16667 14.1667V5.83333C9.16667 4.91667 9.49306 4.13194 10.1458 3.47917C10.7986 2.82639 11.5833 2.5 12.5 2.5C13.4167 2.5 14.2014 2.82639 14.8542 3.47917C15.5069 4.13194 15.8333 4.91667 15.8333 5.83333V12.6458C16.3194 12.8264 16.7188 13.1285 17.0312 13.5521C17.3438 13.9757 17.5 14.4583 17.5 15C17.5 15.6944 17.2569 16.2847 16.7708 16.7708C16.2847 17.2569 15.6944 17.5 15 17.5C14.3056 17.5 13.7153 17.2569 13.2292 16.7708C12.7431 16.2847 12.5 15.6944 12.5 15C12.5 14.4583 12.6562 13.9722 12.9688 13.5417C13.2812 13.1111 13.6806 12.8125 14.1667 12.6458V5.83333C14.1667 5.375 14.0035 4.98264 13.6771 4.65625C13.3507 4.32986 12.9583 4.16667 12.5 4.16667C12.0417 4.16667 11.6493 4.32986 11.3229 4.65625C10.9965 4.98264 10.8333 5.375 10.8333 5.83333V14.1667C10.8333 15.0833 10.5069 15.8681 9.85417 16.5208C9.20139 17.1736 8.41667 17.5 7.5 17.5ZM5 5.83333C5.23611 5.83333 5.43403 5.75347 5.59375 5.59375C5.75347 5.43403 5.83333 5.23611 5.83333 5C5.83333 4.76389 5.75347 4.56597 5.59375 4.40625C5.43403 4.24653 5.23611 4.16667 5 4.16667C4.76389 4.16667 4.56597 4.24653 4.40625 4.40625C4.24653 4.56597 4.16667 4.76389 4.16667 5C4.16667 5.23611 4.24653 5.43403 4.40625 5.59375C4.56597 5.75347 4.76389 5.83333 5 5.83333ZM15 15.8333C15.2361 15.8333 15.434 15.7535 15.5938 15.5938C15.7535 15.434 15.8333 15.2361 15.8333 15C15.8333 14.7639 15.7535 14.566 15.5938 14.4062C15.434 14.2465 15.2361 14.1667 15 14.1667C14.7639 14.1667 14.566 14.2465 14.4062 14.4062C14.2465 14.566 14.1667 14.7639 14.1667 15C14.1667 15.2361 14.2465 15.434 14.4062 15.5938C14.566 15.7535 14.7639 15.8333 15 15.8333Z" fill="white"/>
            </svg>
          </div>
        </el-tooltip>

        <!-- State 2: Expanded - Drop Zone with Delete Button -->
        <div
          v-else
          class="logic-slot-expanded"
          :class="{ 'logic-slot-expanded--drag-over': isDragOver }"
          @mouseenter="handleExpandedMouseEnter"
          @mouseleave="handleExpandedMouseLeave"
          @drop="handleDrop"
          @dragover="handleDragOver"
          @dragleave="handleDragLeave"
        >
          <div class="default-logic-slot-text">
            Drag and drop a logic block here
          </div>
          
          <!-- Delete Button -->
          <el-tooltip
            :visible="isDeleteHovering"
            placement="right"
            effect="dark"
            :offset="8"
            popper-class="logic-slot-tooltip"
          >
            <template #content>
              <div class="tooltip-content">Delete logic slot.</div>
            </template>
            <div
              class="delete-button"
              :class="{ 'delete-button--hover': isDeleteHovering, 'delete-button--active': isDeleteActive }"
              @mouseenter="handleDeleteMouseEnter"
              @mouseleave="handleDeleteMouseLeave"
              @mousedown="handleDeleteMouseDown"
              @mouseup="handleDeleteMouseUp"
              @click="handleDeleteClick"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z" fill="white"/>
              </svg>
            </div>
          </el-tooltip>
        </div>
    </div>
  </EdgeLabelRenderer>
</template>

<script>
import { getBezierPath, EdgeLabelRenderer } from '@vue-flow/core';

export default {
  name: 'CustomConnectionLogic',
  components: {
    EdgeLabelRenderer
  },
  inheritAttrs: false,
  props: {
    id: {
      type: String,
      required: true
    },
    sourceX: {
      type: Number,
      required: true
    },
    sourceY: {
      type: Number,
      required: true
    },
    targetX: {
      type: Number,
      required: true
    },
    targetY: {
      type: Number,
      required: true
    },
    sourcePosition: {
      type: String,
      default: 'bottom'
    },
    targetPosition: {
      type: String,
      default: 'top'
    },
    markerEnd: {
      type: String,
      default: ''
    },
    data: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['logic-card-click', 'logic-card-dropped'],
  data() {
    return {
      isHovering: false,
      isExpanded: false,
      isDeleteHovering: false,
      isDeleteActive: false,
      isDragOver: false, // Whether dragging over the drop zone
      // Logic card state
      hasLogicCard: false, // Whether a logic card has been dropped
      logicCardType: 'wait', // 'wait' or 'review'
      logicCardConfigured: false, // Whether the logic card is configured
      isLogicCardHovering: false, // Whether hovering over the logic card
      isLogicCardSelected: false // Whether the logic card is selected/clicked
    };
  },
  computed: {
    edgePath() {
      const [path] = getBezierPath({
        sourceX: this.sourceX,
        sourceY: this.sourceY,
        sourcePosition: this.sourcePosition,
        targetX: this.targetX,
        targetY: this.targetY,
        targetPosition: this.targetPosition
      });
      return path;
    },
    edgeStyle() {
      return {
        stroke: '#6F4999',
        strokeWidth: 4,
        fill: 'none'
      };
    },
    centerX() {
      return (this.sourceX + this.targetX) / 2;
    },
    centerY() {
      return (this.sourceY + this.targetY) / 2;
    },
    slotWidth() {
      if (this.hasLogicCard) return 90;
      return this.isExpanded ? 84 : 32;
    },
    slotHeight() {
      if (this.hasLogicCard) return 90;
      return this.isExpanded ? 84 : 32;
    },
    warningIconUrl() {
      return 'https://www.figma.com/api/mcp/asset/2c675bc3-e035-404b-b61a-cb06751a1139';
    },
    logicCardLabel() {
      return this.logicCardType === 'wait' ? 'Wait' : 'View & Approval';
    },
    isLogicCardConfigured() {
       return this.data?.logicCardConfigured || false;
    } 
  },
  mounted() {
    // Check if edge has logic card data
    if (this.data && this.data.logicCardType) {
      this.hasLogicCard = true;
      this.logicCardType = this.data.logicCardType;
      this.logicCardConfigured = this.data.logicCardConfigured || false;
    }
    
    // Add global click listener to deselect when clicking outside
    document.addEventListener('click', this.handleGlobalClick);
    
    // Add listener for logic card selection events from other cards
    document.addEventListener('logic-card-selected', this.handleLogicCardSelected);
  },
  beforeUnmount() {
    // Clean up global click listener
    document.removeEventListener('click', this.handleGlobalClick);
    
    // Clean up logic card selection listener
    document.removeEventListener('logic-card-selected', this.handleLogicCardSelected);
  },
  methods: {
    handleMouseEnter() {
      if (!this.isExpanded) {
        this.isHovering = true;
      }
    },
    handleMouseLeave() {
      this.isHovering = false;
    },
    handleSlotClick() {
      this.isExpanded = true;
      this.isHovering = false;
    },
    handleExpandedMouseEnter() {
      // Keep expanded state
    },
    handleExpandedMouseLeave() {
      // Keep expanded state
    },
    handleDeleteMouseEnter() {
      this.isDeleteHovering = true;
    },
    handleDeleteMouseLeave() {
      this.isDeleteHovering = false;
      this.isDeleteActive = false;
    },
    handleDeleteMouseDown() {
      this.isDeleteActive = true;
    },
    handleDeleteMouseUp() {
      this.isDeleteActive = false;
    },
    handleDeleteClick() {
      this.isExpanded = false;
      this.isDeleteHovering = false;
      this.isDeleteActive = false;
    },
    handleLogicCardMouseEnter() {
      this.isLogicCardHovering = true;
    },
    handleLogicCardMouseLeave() {
      this.isLogicCardHovering = false;
    },
    handleLogicCardClick(event) {
      // Stop propagation to prevent global click handler from immediately deselecting
      event.stopPropagation();
      
      // Emit event to notify parent that this logic card was clicked
      // This allows other logic cards to be deselected
      const customEvent = new CustomEvent('logic-card-selected', {
        detail: { edgeId: this.id },
        bubbles: true
      });
      event.target.dispatchEvent(customEvent);
      
      this.isLogicCardSelected = !this.isLogicCardSelected;
      
      // Emit to parent (WorkflowCanvas) to open editor
      this.$emit('logic-card-click', {
        edgeId: this.id,
        logicType: this.logicCardType,
        edgeData: this.data
      });
    },
    handleGlobalClick(event) {
      // Deselect logic card when clicking anywhere else
      if (this.isLogicCardSelected) {
        this.isLogicCardSelected = false;
      }
    },
    handleLogicCardSelected(event) {
      // Deselect this card if another logic card was selected
      if (event.detail.edgeId !== this.id && this.isLogicCardSelected) {
        this.isLogicCardSelected = false;
      }
    },
    handleDragOver(event) {
      event.preventDefault();
      event.stopPropagation();
      this.isDragOver = true;
      event.dataTransfer.dropEffect = 'move';
    },
    handleDragLeave(event) {
      event.preventDefault();
      event.stopPropagation();
      this.isDragOver = false;
    },
    handleDrop(event) {
      event.preventDefault();
      event.stopPropagation();
      this.isDragOver = false;
      
      // Get the dragged block data
      let draggedBlock;
      try {
        const dataString = event.dataTransfer.getData('application/vueflow');
        draggedBlock = JSON.parse(dataString);
      } catch (error) {
        console.error('Error parsing dragged data:', error);
        return;
      }
      
      // Validate that this is a logic block
      if (!draggedBlock || draggedBlock.blockType !== 'logic') {
        console.log('Not a logic block');
        return;
      }
      
      // Map logic block ID to logic type
      const logicTypeMap = {
        'l1': 'wait',
        'l2': 'review'
      };
      
      const logicType = logicTypeMap[draggedBlock.id];
      if (!logicType) {
        console.error('Unknown logic block type:', draggedBlock.id);
        return;
      }
      
      // Update the edge data with logic card information
      this.hasLogicCard = true;
      this.logicCardType = logicType;
      this.logicCardConfigured = false;
      this.isExpanded = false;
      
      // Emit event to parent to update edge data
      this.$emit('logic-card-dropped', {
        edgeId: this.id,
        logicCardType: logicType,
        logicCardConfigured: false
      });
      
      console.log('Logic card dropped:', {
        edgeId: this.id,
        logicType: logicType
      });
    }
  }
};
</script>

<style scoped>
.logic-slot-container {
  position: absolute;
  pointer-events: all;
  z-index: 1000;
}

/* Default State - Icon Button */
.logic-slot-button {
  width: 32px;
  height: 32px;
  background: #6F4999;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.logic-slot-button--hover {
  background: #DBBBFF;
  border: 1px solid #E1D3F1;
  width: 34px;
  height: 34px;
  margin: -1px;
}

.logic-slot-button svg {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.logic-slot-button--hover svg path {
  fill: #6F4999;
}

/* Expanded State - Drop Zone */
.logic-slot-expanded {
  width: 84px;
  height: 84px;
  background: #E1D3F1;
  border: 2px solid #DBBBFF;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  transition: all 0.2s ease;
}

.logic-slot-expanded--drag-over {
  background: #DBBBFF;
  border: 2px solid #6F4999;
  border-style: dashed;
}

/* Delete Button */
.delete-button {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 24px;
  height: 24px;
  background: #D10000;
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.delete-button--hover {
  background: #F63B3B;
  width: 28px;
  height: 28px;
  top: -10px;
  right: -10px;
}

.delete-button--active {
  background: #A60B0B;
}

.delete-button svg {
  width: 14px;
  height: 14px;
}

/* Logic Card Display States */
.logic-card {
  width: 90px;
  height: 90px;
  border-radius: 20px;
  border: 1px solid;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

/* Not Configured State */
.logic-card--not-configured {
  background: #FEF9E9;
  border-color: #D10000;
}

.logic-card--not-configured.logic-card--hover,
.logic-card--not-configured.logic-card--selected {
  border-color: #004879;
}

/* Configured State */
.logic-card--configured {
  background: #E1D3F1;
  border-color: #DBBBFF;
}

.logic-card--configured.logic-card--hover,
.logic-card--configured.logic-card--selected {
  border-color: #004879;
}

.logic-card-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  flex: 1;
  min-width: 0;
}

.logic-card-icon-wrapper {
  width: 34px;
  height: 34px;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.2s ease;
}

/* Icon background colors based on state */
.logic-card--not-configured .logic-card-icon-wrapper {
  background: #FEB52E;
}

.logic-card--not-configured.logic-card--hover .logic-card-icon-wrapper,
.logic-card--not-configured.logic-card--selected .logic-card-icon-wrapper {
  background: #004879;
}

.logic-card--configured .logic-card-icon-wrapper {
  background: #6F4999;
}

.logic-card--configured.logic-card--hover .logic-card-icon-wrapper,
.logic-card--configured.logic-card--selected .logic-card-icon-wrapper {
  background: #004879;
}

.logic-card-icon {
  width: auto;
  height: 20px;
  flex-shrink: 0;
}

.logic-card-label {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.4px;
  color: #374957;
  text-align: center;
  width: 100%;
}

/* Bold label for hover and selected states */
.logic-card--hover .logic-card-label,
.logic-card--selected .logic-card-label {
  font-weight: 700;
}

/* Warning Icon */
.logic-card-warning {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 30px;
  height: 30px;
}

.tooltip-anchor {
  position: absolute;
  width: 0;
  height: 0;
}
</style>

<style>
/* Global tooltip styles */
.logic-slot-tooltip {
  background: #666 !important;
  border-radius: 2px !important;
  padding: 2px 16px !important;
}

.logic-slot-tooltip .el-popper__arrow::before {
  background: #666 !important;
  border: none !important;
}

.logic-slot-tooltip .tooltip-content {
  font-family: 'IBM Plex Sans Hebrew', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.4px;
  color: #FFFFFF;
}

.default-logic-slot-text{
    color: var(--Purple-70, #6F4999);
    padding: 0px 6px;
    text-align: center;
    font-family: Inter;
    font-size: 10px;
    font-style: normal;
    font-weight: 400;
    line-height: 12px; /* 120% */
    letter-spacing: 0.2px;
}
</style>
